import { GET, POST } from 'js/constants/requestMethods';
import { CALL_PRIVATE_API } from 'js/constants/api';
import { pipelineSchema } from '../../schemas';
import * as actionTypes from '../../constants/actionTypes';
import { DispatchType } from '@nexla/@types/redux';
import { isEmpty, pick } from 'lodash';
import { processSearchFilters } from '../../lib/SearchFilterUtils';
import { AvailableFilterTypesByView, SearchFilterViews } from '../../constants/visibilityFilters';

const SET_SCHEMA = true;
const WITH_PROJECTS = false;
const WITHOUT_PROJECTS = true;

type ActionParams = {
  page?: number;
  sort?: { sortKey: string; sortOrder: string };
  per_page?: number;
  agentic?: boolean;
};

/**
 * false = Returns flow with projects
 * true = Returns flow without projects
 * @param withoutProjects:boolean
 * @param types:string[]
 * @param setSchema:boolean @default false, not store the data in redux store, true = store the data in redux store
 */
function setupAction(withoutProjects: boolean, types: string[], setSchema = false) {
  return (params: ActionParams = {}) =>
    (dispatch: DispatchType, getState: any) => {
      const {
        page = 1,
        per_page = 100,
        sort = { sortKey: 'created_at', sortOrder: 'desc' },
        agentic = false,
      } = params;

      const {
        app: {
          accessControl,
          visibilityFilters: { filters, availableFilters },
        },
      } = getState();

      const aclQueryMode = accessControl.mode;

      const sortKey = sort.sortKey;
      const sortOrder = sort.sortOrder;
      const perPage = per_page;

      const availableFilterTypes = AvailableFilterTypesByView[SearchFilterViews.PROJECT_PIPELINES];
      const { processedFilters } = processSearchFilters(
        filters[SearchFilterViews.PROJECT_PIPELINES],
        pick(availableFilters, availableFilterTypes),
      );

      let requestMethod: string, endpoint: string, payload: Record<string, any>;

      const isCountRequest =
        types.includes(actionTypes.GET_FLOWS_WITHOUT_PROJECTS_COUNT_REQUEST) ||
        types.includes(actionTypes.GET_FLOWS_WITH_PROJECTS_COUNT_REQUEST);

      /**
       * If there are no filters, we use the GET endpoint
       * If there are filters, we use the POST endpoint
       * If it's a count request, we use the GET endpoint, do not apply filters on count requests
       */

      if (isEmpty(processedFilters) || isCountRequest) {
        requestMethod = GET;
        endpoint = '/flows';
        payload = {
          page,
          per_page: perPage,
          sort_by: sortKey,
          sort_order: sortOrder,
          without_projects: withoutProjects ? 1 : 0,
          // Agentic (MCP Studio) flows filtering controlled via params
          agentic,
        };

        /**
         * For count requests, we do not need the data response
         * we are only interested in the count of flows from metadata
         */
        if (isCountRequest) {
          payload = {
            page: 1,
            per_page: 1,
            without_projects: withoutProjects ? 1 : 0,
            agentic,
          };
        }
      } else {
        requestMethod = POST;
        const searchPage = page !== undefined ? page : 1;
        endpoint = `/flows/search?page=${searchPage}&per_page=${perPage}&sort_by=${sortKey}&sort_order=${sortOrder}&without_projects=${withoutProjects ? 1 : 0}&agentic=${agentic}`;

        // If 'and' is already in filters, don't add it again
        if (processedFilters.includes('and')) {
          payload = {
            filters: [...processedFilters, { field: 'project_id', operator: 'empty', value: '' }],
          };
        } else {
          payload = {
            filters: [
              'and',
              ...processedFilters,
              { field: 'project_id', operator: 'empty', value: '' },
            ],
          };
        }
      }

      const body = {
        types,
        endpoint,
        payload,
        schema: null,
        requestMethod,
        aclQueryMode,
      };

      if (setSchema) {
        body.schema = pipelineSchema;
      }

      return dispatch({ [CALL_PRIVATE_API]: body }).catch((error: Error) => {
        const functionName = withoutProjects ? 'loadFlowsWithoutProjects' : 'loadFlowsWithProjects';
        console.error(`${functionName} error:`, error);
        throw error;
      });
    };
}

export const loadFlowsWithProjectsCount = setupAction(WITH_PROJECTS, [
  actionTypes.GET_FLOWS_WITH_PROJECTS_REQUEST,
  actionTypes.GET_FLOWS_WITH_PROJECTS_COUNT_SUCCESS,
  actionTypes.GET_FLOWS_WITH_PROJECTS_COUNT_FAILURE,
]);

export const loadFlowsWithoutProjectsCount = setupAction(WITHOUT_PROJECTS, [
  actionTypes.GET_FLOWS_WITHOUT_PROJECTS_COUNT_REQUEST,
  actionTypes.GET_FLOWS_WITHOUT_PROJECTS_COUNT_SUCCESS,
  actionTypes.GET_FLOWS_WITHOUT_PROJECTS_COUNT_FAILURE,
]);

export const loadFlowsWithProjects = setupAction(
  WITH_PROJECTS,
  [
    actionTypes.GET_FLOWS_WITH_PROJECTS_REQUEST,
    actionTypes.GET_FLOWS_WITH_PROJECTS_SUCCESS,
    actionTypes.GET_FLOWS_WITH_PROJECTS_FAILURE,
  ],
  SET_SCHEMA,
);

export const loadFlowsWithoutProjects = setupAction(
  WITHOUT_PROJECTS,
  [
    actionTypes.GET_FLOWS_WITHOUT_PROJECTS_REQUEST,
    actionTypes.GET_FLOWS_WITHOUT_PROJECTS_SUCCESS,
    actionTypes.GET_FLOWS_WITHOUT_PROJECTS_FAILURE,
  ],
  SET_SCHEMA,
);

export const cleanupFlowsWithoutProjects = () => {
  return {
    type: actionTypes.GET_FLOWS_WITHOUT_PROJECTS_CLEANUP,
  };
};
